import React, { useState } from 'react';
import { 
    X, 
    User, 
    Phone, 
    Target, 
    MessageSquare, 
    FileText,
    Plus,
    Hash
} from 'lucide-react';
import client from '../api/client';
import { toast } from 'react-hot-toast';

const LeadModal = ({ isOpen, onClose, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        phone: '',
        username: '',
        source: 'Manual Entry',
        niche: '',
        notes: ''
    });

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await client.post('/crm/leads', formData);
            toast.success('Лид успешно добавлен');
            setFormData({
                first_name: '',
                last_name: '',
                phone: '',
                username: '',
                source: 'Manual Entry',
                niche: '',
                notes: ''
            });
            onSuccess();
        } catch (err) {
            console.error('Error creating lead', err);
            toast.error(err.response?.data?.detail || 'Ошибка при создании лида');
        } finally {
            setLoading(false);
        }
    };

    const InputField = ({ label, icon: Icon, type = "text", name, placeholder, required = false }) => (
        <div className="space-y-1.5">
            <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest ml-1">{label}</label>
            <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600 group-focus-within:text-blue-500 transition-colors">
                    <Icon size={16} />
                </div>
                <input
                    type={type}
                    required={required}
                    value={formData[name]}
                    onChange={(e) => setFormData({ ...formData, [name]: e.target.value })}
                    placeholder={placeholder}
                    className="w-full bg-zinc-900/50 border border-white/5 rounded-2xl pl-12 pr-4 py-3.5 text-sm text-white placeholder:text-zinc-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all"
                />
            </div>
        </div>
    );

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
            
            <div className="relative w-full max-w-xl bg-zinc-950 border border-white/10 rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
                {/* Header */}
                <div className="px-8 py-6 border-b border-white/5 flex justify-between items-center bg-zinc-900/20">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-blue-600/10 text-blue-500 rounded-xl border border-blue-500/20">
                            <Plus size={20} />
                        </div>
                        <div>
                            <h2 className="text-xl font-black text-white uppercase tracking-tight">Новый Лид</h2>
                            <p className="text-[9px] font-black text-zinc-600 uppercase tracking-widest mt-0.5">Ручное добавление в CRM систему</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/5 text-zinc-500 hover:text-white rounded-xl transition-all">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-8 space-y-6 max-h-[70vh] overflow-y-auto scrollbar-hide">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <InputField 
                            label="Имя" 
                            name="first_name" 
                            icon={User} 
                            placeholder="Напр. Иван" 
                            required 
                        />
                        <InputField 
                            label="Фамилия" 
                            name="last_name" 
                            icon={User} 
                            placeholder="Напр. Иванов" 
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <InputField 
                            label="Телефон" 
                            name="phone" 
                            icon={Phone} 
                            placeholder="+7 (999) ..." 
                        />
                        <InputField 
                            label="Telegram Username" 
                            name="username" 
                            icon={MessageSquare} 
                            placeholder="без @" 
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <InputField 
                            label="Источник" 
                            name="source" 
                            icon={Target} 
                            placeholder="Напр. Офлайн встреча" 
                        />
                        <InputField 
                            label="Ниша / Бизнес" 
                            name="niche" 
                            icon={Hash} 
                            placeholder="Напр. Логистика" 
                        />
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest ml-1">Заметки / Комментарии</label>
                        <div className="relative group">
                            <div className="absolute left-4 top-4 text-zinc-600 group-focus-within:text-blue-500 transition-colors">
                                <FileText size={16} />
                            </div>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                placeholder="Дополнительная информация о лиде..."
                                className="w-full bg-zinc-900/50 border border-white/5 rounded-2xl pl-12 pr-4 py-4 text-sm text-white placeholder:text-zinc-700 focus:border-blue-500 outline-none transition-all min-h-[120px] resize-none"
                            />
                        </div>
                    </div>

                    <div className="pt-4">
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-4 bg-blue-600 text-white rounded-2xl font-black uppercase text-[11px] tracking-widest hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-xl shadow-blue-600/20 flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                            ) : (
                                <><Plus size={16} /> Создать Лид</>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default LeadModal;
